"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, ChangeEvent, DragEvent, KeyboardEvent } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faCheck,
  faCircleQuestion,
  faCirclePlay,
  faHeadset,
  faListCheck,
  faPenToSquare,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "../ui/Tooltip";
import { Modal, Input, Textarea, FormGroup } from "@/components/ui";
import { useToast } from "@/components/ui/ToastContainer";
import { useUser } from "@/contexts/UserContext";
import { ChatDrawer } from "@/components/chat/ChatDrawer";
import { SOFTPHONE_OPEN_EVENT } from "@/components/telephony/TelephonyHost";
import { SOFTPHONE_INCOMING_EVENT } from "@/components/telephony/events";
import { useChatRealtime } from "@/contexts/ChatRealtimeContext";
import "@/styles/chat-drawer.css";

const API_BASE =
  process.env.NEXT_PUBLIC_API_URL || "https://app.canerozen.com/api";
const ORDER_FOLLOW_TOUR_KEY = "order_follow_bottom_tour_seen_v1";

type SupportAttachmentDraft = {
  id: number;
  name: string;
  size: number;
  mime: string;
  dataUrl: string;
};

type NavFaqItem = {
  id: number;
  question: string;
  answer_html: string;
  sort: number;
  created_at?: number;
  updated_at?: number;
};

type NavFaqState = {
  nav_id: number | null;
  name: string;
  url: string;
  faqs: NavFaqItem[];
  has_faqs: boolean;
  can_manage: boolean;
};

type NavFaqUpload = {
  name: string;
  path: string;
  size: number;
  created_at: number;
};

type OrderFollowState = {
  visible: boolean;
  orderId?: string;
  isFollowing?: boolean;
  followerCount?: number;
  saving?: boolean;
};

type OrderFollowTourPosition = {
  left: number;
  arrowLeft: number;
};

function uploadAssetUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("Library/")) return `https://farktorcdn.com/${path}`;
  const base = API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function formatBytes(size: number) {
  if (!Number.isFinite(size) || size <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const value = size / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function getInitials(name: string) {
  const n = String(name || "").trim();
  if (!n) return "?";
  const parts = n.split(/\s+/).filter(Boolean);
  const a = (parts[0] || "").slice(0, 1);
  const b = (parts[1] || "").slice(0, 1);
  return (a + b).toUpperCase() || a.toUpperCase() || "?";
}

function avatarColor(seed: string) {
  const s = String(seed || "")
    .trim()
    .toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  const hue = h % 360;
  return `hsl(${hue} 62% 32%)`;
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function FaqAnswerEditor({
  value,
  onChange,
  onOpenUploadBox,
}: {
  value: string;
  onChange: (value: string) => void;
  onOpenUploadBox: () => void;
}) {
  const [mode, setMode] = useState<"design" | "source">("design");
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mode !== "design" || !editorRef.current) return;
    if (editorRef.current.innerHTML !== value)
      editorRef.current.innerHTML = value || "";
  }, [mode, value]);

  const execCommand = (cmd: string, commandValue?: string) => {
    try {
      document.execCommand(cmd, false, commandValue);
      editorRef.current?.focus();
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    } catch {
      // ignore editor command failures
    }
  };

  return (
    <div className="bb-faq-editor">
      <div className="bb-faq-editor__toolbar">
        <div className="bb-faq-editor__tools">
          <button
            type="button"
            onClick={() => execCommand("bold")}
            title="Kalın"
            disabled={mode === "source"}
          >
            <i className="fa-solid fa-bold" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("italic")}
            title="İtalik"
            disabled={mode === "source"}
          >
            <i className="fa-solid fa-italic" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("underline")}
            title="Altı çizili"
            disabled={mode === "source"}
          >
            <i className="fa-solid fa-underline" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("insertUnorderedList")}
            title="Liste"
            disabled={mode === "source"}
          >
            <i className="fa-solid fa-list-ul" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("insertOrderedList")}
            title="Numaralı liste"
            disabled={mode === "source"}
          >
            <i className="fa-solid fa-list-ol" />
          </button>
          <button
            type="button"
            onClick={() => execCommand("formatBlock", "h3")}
            title="Başlık"
            disabled={mode === "source"}
          >
            <i className="fa-solid fa-heading" />
          </button>
        </div>
        <div className="bb-faq-editor__tools bb-faq-editor__tools--right">
          <button
            type="button"
            className="bb-faq-editor__upload"
            onClick={onOpenUploadBox}
          >
            <i className="fa-solid fa-cloud-arrow-up" /> Uploadbox
          </button>
          <button
            type="button"
            className={mode === "source" ? "is-active" : ""}
            onClick={() =>
              setMode((current) => (current === "source" ? "design" : "source"))
            }
            title="Kaynak kod"
          >
            <i className="fa-solid fa-code" />
          </button>
        </div>
      </div>
      {mode === "design" ? (
        <div
          ref={editorRef}
          className="bb-faq-editor__surface"
          contentEditable
          suppressContentEditableWarning
          onInput={() => onChange(editorRef.current?.innerHTML || "")}
        />
      ) : (
        <textarea
          className="bb-faq-editor__source"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}

export function BottomBar() {
  const { user } = useUser();
  const { showToast } = useToast();
  const { onlineUsers, unreadTotal, clearUnreadOptimistic, openDrawer } =
    useChatRealtime();

  const pathname = usePathname();

  const [isQuickTaskOpen, setIsQuickTaskOpen] = useState(false);
  const [quickTaskTitle, setQuickTaskTitle] = useState("");
  const [quickTaskDescription, setQuickTaskDescription] = useState("");
  const [quickTaskError, setQuickTaskError] = useState("");
  const [isQuickTaskAdding, setIsQuickTaskAdding] = useState(false);

  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [hasIncomingCall, setHasIncomingCall] = useState(false);
  const [santralActive, setSantralActive] = useState<boolean | null>(null);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportFiles, setSupportFiles] = useState<SupportAttachmentDraft[]>(
    [],
  );
  const [supportError, setSupportError] = useState("");
  const [isSupportSubmitting, setIsSupportSubmitting] = useState(false);
  const [faqState, setFaqState] = useState<NavFaqState | null>(null);
  const [isFaqOpen, setIsFaqOpen] = useState(false);
  const [isFaqEditorOpen, setIsFaqEditorOpen] = useState(false);
  const [isFaqLoading, setIsFaqLoading] = useState(false);
  const [isFaqSaving, setIsFaqSaving] = useState(false);
  const [editingFaq, setEditingFaq] = useState<NavFaqItem | null>(null);
  const [deletingFaq, setDeletingFaq] = useState<NavFaqItem | null>(null);
  const [faqQuestion, setFaqQuestion] = useState("");
  const [faqAnswer, setFaqAnswer] = useState("");
  const [faqError, setFaqError] = useState("");
  const [isFaqQuestionOpen, setIsFaqQuestionOpen] = useState(false);
  const [newFaqQuestion, setNewFaqQuestion] = useState("");
  const [newFaqQuestionError, setNewFaqQuestionError] = useState("");
  const [draggedFaqId, setDraggedFaqId] = useState<number | null>(null);
  const [isFaqUploadOpen, setIsFaqUploadOpen] = useState(false);
  const [faqUploads, setFaqUploads] = useState<NavFaqUpload[]>([]);
  const [isFaqUploadLoading, setIsFaqUploadLoading] = useState(false);
  const [orderFollow, setOrderFollow] = useState<OrderFollowState | null>(null);
  const [showOrderFollowTour, setShowOrderFollowTour] = useState(false);
  const [orderFollowTourPosition, setOrderFollowTourPosition] =
    useState<OrderFollowTourPosition | null>(null);

  const quickTaskRef = useRef<HTMLInputElement | null>(null);
  const supportFileRef = useRef<HTMLInputElement | null>(null);
  const faqUploadFileRef = useRef<HTMLInputElement | null>(null);
  const isOrderDetailPage = pathname === "/pages/order/view";
  const isAdmin = user?.admin === 1 || String(user?.admin ?? "") === "1";

  const authHeaders = useCallback((json = false) => {
    const token = localStorage.getItem("auth_token") || "";
    return {
      ...(json ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchNavFaq = useCallback(async () => {
    if (!user || !pathname) return;
    try {
      setIsFaqLoading(true);
      const response = await fetch(
        `${API_BASE}/admin/nav-faq?url=${encodeURIComponent(pathname)}`,
        {
          headers: authHeaders(),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success)
        throw new Error(data?.message || "SSS bilgisi alınamadı");
      setFaqState(data.data || null);
    } catch (error: unknown) {
      setFaqState(null);
      console.error("nav faq fetch error:", error);
    } finally {
      setIsFaqLoading(false);
    }
  }, [authHeaders, pathname, user]);

  useEffect(() => {
    fetchNavFaq();
  }, [fetchNavFaq]);

  useEffect(() => {
    if (!user) {
      setSantralActive(null);
      return;
    }
    let cancelled = false;
    async function fetchSantralState() {
      try {
        const response = await fetch(`${API_BASE}/call-center/eligibility`, {
          headers: authHeaders(),
          cache: "no-store",
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        setSantralActive(Boolean(response.ok && data?.success !== false && data?.data?.santralActive));
      } catch {
        if (!cancelled) setSantralActive(false);
      }
    }
    fetchSantralState();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, user]);

  useEffect(() => {
    if (!isOrderDetailPage) {
      setOrderFollow(null);
      setShowOrderFollowTour(false);
      return;
    }
    const handleFollowState = (event: Event) => {
      const next = (event as CustomEvent<OrderFollowState>).detail;
      if (!next?.visible) {
        setOrderFollow(null);
        return;
      }
      setOrderFollow({
        visible: true,
        orderId: String(next.orderId || ""),
        isFollowing: Boolean(next.isFollowing),
        followerCount: Number(next.followerCount || 0),
        saving: Boolean(next.saving),
      });
    };
    window.addEventListener("order-detail:follow-state", handleFollowState);
    return () =>
      window.removeEventListener("order-detail:follow-state", handleFollowState);
  }, [isOrderDetailPage]);

  useEffect(() => {
    if (!isOrderDetailPage || !orderFollow?.visible) return;
    if (localStorage.getItem(ORDER_FOLLOW_TOUR_KEY) === "1") return;
    const timer = window.setTimeout(() => setShowOrderFollowTour(true), 650);
    return () => window.clearTimeout(timer);
  }, [isOrderDetailPage, orderFollow?.visible]);

  const updateOrderFollowTourPosition = useCallback(() => {
    const target = document.querySelector<HTMLElement>("[data-order-follow-entry='true']");
    if (!target) {
      setOrderFollowTourPosition(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const margin = 14;
    const tourWidth = Math.min(340, Math.max(0, viewportWidth - margin * 2));
    const targetCenter = rect.left + rect.width / 2;
    const maxLeft = Math.max(margin, viewportWidth - tourWidth - margin);
    const left = Math.min(Math.max(targetCenter - tourWidth / 2, margin), maxLeft);
    const arrowLeft = Math.min(Math.max(targetCenter - left - 6, 14), tourWidth - 26);

    setOrderFollowTourPosition({ left, arrowLeft });
  }, []);

  useEffect(() => {
    if (!showOrderFollowTour || !isOrderDetailPage || !orderFollow?.visible) {
      setOrderFollowTourPosition(null);
      return;
    }

    updateOrderFollowTourPosition();
    const frame = window.requestAnimationFrame(updateOrderFollowTourPosition);
    window.addEventListener("resize", updateOrderFollowTourPosition);
    window.addEventListener("scroll", updateOrderFollowTourPosition, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateOrderFollowTourPosition);
      window.removeEventListener("scroll", updateOrderFollowTourPosition, true);
    };
  }, [
    isOrderDetailPage,
    orderFollow?.visible,
    showOrderFollowTour,
    updateOrderFollowTourPosition,
  ]);

  const dismissOrderFollowTour = useCallback(() => {
    localStorage.setItem(ORDER_FOLLOW_TOUR_KEY, "1");
    setShowOrderFollowTour(false);
  }, []);

  const toggleOrderFollow = useCallback(() => {
    window.dispatchEvent(new CustomEvent("order-detail:toggle-follow"));
  }, []);

  const resetSupportForm = useCallback(() => {
    setSupportMessage("");
    setSupportFiles([]);
    setSupportError("");
  }, []);

  const openSupportForm = useCallback(() => {
    setIsSupportOpen(true);
    setSupportError("");
  }, []);

  const openSoftphone = useCallback(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: SOFTPHONE_OPEN_EVENT }, window.location.origin);
      return;
    }
    window.dispatchEvent(new Event(SOFTPHONE_OPEN_EVENT));
  }, []);

  useEffect(() => {
    const handleIncomingCall = (event: Event) => {
      const detail = (event as CustomEvent<{ incoming?: boolean }>).detail;
      setHasIncomingCall(Boolean(detail?.incoming));
    };
    window.addEventListener(SOFTPHONE_INCOMING_EVENT, handleIncomingCall);
    return () => window.removeEventListener(SOFTPHONE_INCOMING_EVENT, handleIncomingCall);
  }, []);

  // Chat drawer state is managed globally by ChatRealtimeProvider.

  const focusQuickTask = useCallback(() => {
    // Double RAF: modal content + transitions can delay DOM readiness
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        quickTaskRef.current?.focus();
      });
    });
  }, []);

  const currentTaskPageTitle = useCallback(() => {
    if (typeof document === "undefined") return pathname || "Sayfa";
    const rawTitle = String(document.title || "").replace(/\s*[-|]\s*Farktör.*$/i, "").trim();
    return rawTitle || pathname || "Sayfa";
  }, [pathname]);

  useEffect(() => {
    if (isQuickTaskOpen) focusQuickTask();
  }, [focusQuickTask, isQuickTaskOpen]);

  const submitQuickTask = useCallback(async () => {
    const title = quickTaskTitle.trim();
    if (!title) {
      setQuickTaskError("Görev adı zorunludur");
      focusQuickTask();
      return;
    }
    if (isQuickTaskAdding) return;

    const token = localStorage.getItem("auth_token");
    if (!token) {
      showToast("Oturum bulunamadı", "error");
      return;
    }

    try {
      setIsQuickTaskAdding(true);
      const response = await fetch(`${API_BASE}/user/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description: quickTaskDescription.trim(),
          page_url: pathname,
          page_title: currentTaskPageTitle(),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        const msg = data?.message || "Görev oluşturulamadı";
        setQuickTaskError(msg);
        showToast(msg, "error");
        focusQuickTask();
        return;
      }

      showToast("Görev eklendi", "success");
      setQuickTaskTitle("");
      setQuickTaskDescription("");
      setQuickTaskError("");
      window.dispatchEvent(new CustomEvent("user-tasks:changed"));
      focusQuickTask();
    } catch (e: unknown) {
      console.error("quick task create error:", e);
      showToast(errorMessage(e, "Görev oluşturulamadı"), "error");
      focusQuickTask();
    } finally {
      setIsQuickTaskAdding(false);
    }
  }, [currentTaskPageTitle, focusQuickTask, isQuickTaskAdding, pathname, quickTaskDescription, quickTaskTitle, showToast]);

  const onSupportFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const list = Array.from(event.target.files || []);
      event.target.value = "";
      if (list.length === 0) return;

      const currentCount = supportFiles.length;
      const maxCount = 5;
      const allowed = Math.max(0, maxCount - currentCount);

      if (allowed <= 0) {
        setSupportError("En fazla 5 dosya ekleyebilirsiniz");
        return;
      }

      const picked = list.slice(0, allowed);
      const nextItems: SupportAttachmentDraft[] = [];

      for (const file of picked) {
        if (file.size > 50 * 1024 * 1024) {
          setSupportError("Dosya boyutu 50MB'ı geçemez");
          continue;
        }

        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(new Error("Dosya okunamadı"));
          reader.readAsDataURL(file);
        }).catch(() => "");

        if (!dataUrl) continue;

        nextItems.push({
          id: Date.now() + Math.floor(Math.random() * 1000),
          name: file.name,
          size: file.size,
          mime: file.type || "application/octet-stream",
          dataUrl,
        });
      }

      if (nextItems.length > 0) {
        setSupportFiles((prev) => [...prev, ...nextItems]);
        setSupportError("");
      }
    },
    [supportFiles.length],
  );

  const removeSupportFile = useCallback((id: number) => {
    setSupportFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const submitSupportRequest = useCallback(async () => {
    const message = supportMessage.trim();

    if (!message) {
      setSupportError("Talep detayı zorunludur");
      return;
    }
    if (isSupportSubmitting) return;

    const token = localStorage.getItem("auth_token");
    if (!token) {
      showToast("Oturum bulunamadı", "error");
      return;
    }

    try {
      setIsSupportSubmitting(true);
      setSupportError("");

      const response = await fetch(`${API_BASE}/user/supports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          description: message,
          attachments: supportFiles.map((f) => ({
            name: f.name,
            mime: f.mime,
            size: f.size,
            dataUrl: f.dataUrl,
          })),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        const msg = data?.message || "Destek talebi oluşturulamadı";
        setSupportError(msg);
        showToast(msg, "error");
        return;
      }

      showToast("Destek talebiniz başarıyla alındı", "success");
      setIsSupportOpen(false);
      resetSupportForm();
    } catch (e: unknown) {
      const msg = errorMessage(e, "Destek talebi oluşturulamadı");
      setSupportError(msg);
      showToast(msg, "error");
    } finally {
      setIsSupportSubmitting(false);
    }
  }, [
    isSupportSubmitting,
    resetSupportForm,
    showToast,
    supportFiles,
    supportMessage,
  ]);

  const faqEnabled = Boolean(isAdmin || faqState?.has_faqs);

  const openFaqPanel = useCallback(() => {
    if (!faqEnabled) return;
    setIsFaqOpen(true);
    fetchNavFaq();
  }, [faqEnabled, fetchNavFaq]);

  const openFaqQuestionModal = useCallback(() => {
    setNewFaqQuestion("");
    setNewFaqQuestionError("");
    setIsFaqQuestionOpen(true);
  }, []);

  const openFaqEditor = useCallback((item: NavFaqItem) => {
    setEditingFaq(item);
    setFaqQuestion(item.question || "");
    setFaqAnswer(item.answer_html || "");
    setFaqError("");
    setIsFaqEditorOpen(true);
  }, []);

  const saveFaqs = useCallback(
    async (nextFaqs: NavFaqItem[]) => {
      if (!faqState?.nav_id) {
        showToast("Bu sayfa için menü kaydı bulunamadı", "error");
        return false;
      }
      try {
        setIsFaqSaving(true);
        const response = await fetch(
          `${API_BASE}/admin/nav-faq/${faqState.nav_id}`,
          {
            method: "PUT",
            headers: authHeaders(true),
            body: JSON.stringify({ faqs: nextFaqs }),
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success)
          throw new Error(data?.message || "SSS kaydedilemedi");
        setFaqState(data.data);
        showToast("SSS güncellendi", "success");
        return true;
      } catch (error: unknown) {
        showToast(errorMessage(error, "SSS kaydedilemedi"), "error");
        return false;
      } finally {
        setIsFaqSaving(false);
      }
    },
    [authHeaders, faqState?.nav_id, showToast],
  );

  const saveNewFaqQuestion = useCallback(async () => {
    const question = newFaqQuestion.trim();
    if (!question) {
      setNewFaqQuestionError("Soru zorunludur");
      return;
    }
    const now = Date.now();
    const current = faqState?.faqs || [];
    const ok = await saveFaqs([
      ...current,
      {
        id: now,
        question,
        answer_html: "",
        sort: current.length + 1,
        created_at: now,
        updated_at: now,
      },
    ]);
    if (ok) {
      setNewFaqQuestion("");
      setNewFaqQuestionError("");
    }
  }, [faqState?.faqs, newFaqQuestion, saveFaqs]);

  const saveFaqItem = useCallback(async () => {
    if (!editingFaq) return;
    const question = faqQuestion.trim();
    if (!question) {
      setFaqError("Soru zorunludur");
      return;
    }
    const now = Date.now();
    const current = faqState?.faqs || [];
    const nextItem: NavFaqItem = {
      id: editingFaq.id,
      question,
      answer_html: faqAnswer,
      sort: editingFaq.sort || current.length + 1,
      created_at: editingFaq.created_at || now,
      updated_at: now,
    };
    const nextFaqs = current.map((item) =>
      item.id === editingFaq.id ? nextItem : item,
    );
    const ok = await saveFaqs(nextFaqs);
    if (ok) setIsFaqEditorOpen(false);
  }, [editingFaq, faqAnswer, faqQuestion, faqState?.faqs, saveFaqs]);

  const deleteFaqItem = useCallback(async () => {
    if (!deletingFaq) return;
    const nextFaqs = (faqState?.faqs || []).filter(
      (row) => row.id !== deletingFaq.id,
    );
    const ok = await saveFaqs(nextFaqs);
    if (ok) setDeletingFaq(null);
  }, [deletingFaq, faqState?.faqs, saveFaqs]);

  const moveFaqItem = useCallback(
    async (dragId: number, targetId: number) => {
      const rows = [...(faqState?.faqs || [])];
      const fromIndex = rows.findIndex((row) => row.id === dragId);
      const toIndex = rows.findIndex((row) => row.id === targetId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
      const [removed] = rows.splice(fromIndex, 1);
      rows.splice(toIndex, 0, removed);
      await saveFaqs(rows);
    },
    [faqState?.faqs, saveFaqs],
  );

  const handleFaqDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>, targetId: number) => {
      event.preventDefault();
      if (!draggedFaqId) return;
      await moveFaqItem(draggedFaqId, targetId);
      setDraggedFaqId(null);
    },
    [draggedFaqId, moveFaqItem],
  );

  const fetchFaqUploads = useCallback(async () => {
    try {
      setIsFaqUploadLoading(true);
      const response = await fetch(`${API_BASE}/admin/nav-faq/uploads`, {
        headers: authHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success)
        throw new Error(data?.message || "Uploadbox alınamadı");
      setFaqUploads(Array.isArray(data.data) ? data.data : []);
    } catch (error: unknown) {
      showToast(errorMessage(error, "Uploadbox alınamadı"), "error");
    } finally {
      setIsFaqUploadLoading(false);
    }
  }, [authHeaders, showToast]);

  const openFaqUploadBox = useCallback(() => {
    setIsFaqUploadOpen(true);
    fetchFaqUploads();
  }, [fetchFaqUploads]);

  const insertFaqImage = useCallback((path: string) => {
    const src = uploadAssetUrl(path);
    setFaqAnswer(
      (current) => `${current || ""}<p><img src="${src}" alt="" /></p>`,
    );
    setIsFaqUploadOpen(false);
  }, []);

  const handleFaqUploadFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        setIsFaqUploadLoading(true);
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(new Error("Görsel okunamadı"));
          reader.readAsDataURL(file);
        });
        const response = await fetch(`${API_BASE}/admin/nav-faq/uploads`, {
          method: "POST",
          headers: authHeaders(true),
          body: JSON.stringify({ name: file.name, dataUrl }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success)
          throw new Error(data?.message || "Görsel yüklenemedi");
        const uploaded = data.data as NavFaqUpload;
        setFaqUploads((current) => [uploaded, ...current]);
        insertFaqImage(uploaded.path);
        showToast("Görsel yüklendi ve cevaba eklendi", "success");
      } catch (error: unknown) {
        showToast(errorMessage(error, "Görsel yüklenemedi"), "error");
      } finally {
        setIsFaqUploadLoading(false);
        if (faqUploadFileRef.current) faqUploadFileRef.current.value = "";
      }
    },
    [authHeaders, insertFaqImage, showToast],
  );

  const orderFollowTourStyle = orderFollowTourPosition
    ? ({
        "--order-follow-tour-left": `${orderFollowTourPosition.left}px`,
        "--order-follow-tour-arrow-left": `${orderFollowTourPosition.arrowLeft}px`,
      } as CSSProperties & Record<string, string>)
    : undefined;

  return (
    <>
      <div className="panel-bottombar">
        <div className="panel-bottombar__left">
          <button className="panel-bottombar__quick-add">
            <span>Hızlı Menü Ekle</span>
          </button>

          <button className="panel-bottombar__tab panel-bottombar__tab--active">
            Ürün Detay
          </button>
          <button className="panel-bottombar__tab">Siparişlerim</button>
          <button className="panel-bottombar__tab">Raporlarım</button>
        </div>

        <div className="panel-bottombar__right">
          {isOrderDetailPage && orderFollow?.visible && (
            <Tooltip
              content={
                orderFollow.isFollowing
                  ? "Takipte: not, iptal talebi, paket ve durum güncellemelerinde bildirim alırsınız."
                  : "Siparişi takip et"
              }
              position="top"
            >
              <button
                className={`panel-bottombar__icon-btn panel-bottombar__order-follow ${
                  orderFollow.isFollowing ? "is-following" : ""
                } ${showOrderFollowTour ? "is-tour-target" : ""}`}
                onClick={toggleOrderFollow}
                disabled={orderFollow.saving}
                aria-label={orderFollow.isFollowing ? "Sipariş takipte" : "Siparişi takip et"}
                data-order-follow-entry="true"
              >
                <FontAwesomeIcon icon={faBell} />
                {orderFollow.isFollowing && (
                  <span className="panel-bottombar__order-follow-check">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                )}
              </button>
            </Tooltip>
          )}

          <Tooltip content="Sık Sorulan Sorular" position="top">
            <button
              className={`panel-bottombar__icon-btn ${faqState?.has_faqs ? "is-active" : ""}`}
              onClick={openFaqPanel}
              disabled={!faqEnabled || isFaqLoading}
              aria-label="Sık Sorulan Sorular"
            >
              <FontAwesomeIcon icon={faCircleQuestion} />
            </button>
          </Tooltip>

          <Tooltip content="Video Eğitim Dökümanı" position="top">
            <button
              className="panel-bottombar__icon-btn"
              disabled={true}
              aria-label="Video Eğitim Dökümanı"
            >
              <FontAwesomeIcon icon={faCirclePlay} />
            </button>
          </Tooltip>

          <Tooltip content="Destek Talepleri" position="top">
            <button
              className="panel-bottombar__icon-btn"
              onClick={openSupportForm}
              aria-label="Destek Talepleri"
            >
              <FontAwesomeIcon icon={faHeadset} />
            </button>
          </Tooltip>

          <Tooltip content="Telefon" position="top">
            <button
              className={`panel-bottombar__icon-btn ${
                hasIncomingCall
                  ? "is-ringing"
                  : santralActive
                    ? "is-phone-active"
                    : santralActive === false
                      ? "is-phone-inactive"
                      : ""
              }`}
              onClick={openSoftphone}
              disabled={!user}
              aria-label="Telefon"
            >
              <FontAwesomeIcon icon={faPhone} />
            </button>
          </Tooltip>

          <Tooltip content="Hızlı Görev Ekle" position="top">
            <button
              className="panel-bottombar__icon-btn"
              onClick={() => {
                setIsQuickTaskOpen(true);
                setQuickTaskError("");
              }}
              disabled={!user}
              aria-label="Hızlı görev ekle"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </button>
          </Tooltip>

          <Tooltip content="Görev Listem" position="top">
            <button
              className="panel-bottombar__icon-btn"
              onClick={() => {
                window.location.href = "/pages/users/task";
              }}
              disabled={!user}
              aria-label="Görev Listem"
            >
              <FontAwesomeIcon icon={faListCheck} />
            </button>
          </Tooltip>

          <div className="panel-bottombar__divider"></div>

          <div
            className="panel-bottombar__online"
            role="button"
            tabIndex={0}
            onClick={() => {
              openDrawer();
              // optimistic: clear badge when user opens chat
              clearUnreadOptimistic();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openDrawer();
                clearUnreadOptimistic();
              }
            }}
            style={{ cursor: "pointer" }}
            title="Sohbeti aç"
          >
            <span
              className="panel-bottombar__online-dot"
              style={{
                background: onlineUsers.length > 0 ? "#10b981" : "#9ca3af",
              }}
            ></span>
            <span>
              {onlineUsers.length > 0
                ? "Çevrimiçi olanlar"
                : "Çevrimiçi kimse yok"}
            </span>
            {unreadTotal > 0 && (
              <span
                style={{
                  marginLeft: 8,
                  minWidth: 18,
                  height: 18,
                  padding: "0 6px",
                  borderRadius: 999,
                  background: "#22c55e",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Okunmamış mesaj"
              >
                {unreadTotal}
              </span>
            )}
          </div>

          <div className="panel-bottombar__avatars">
            {onlineUsers.length === 0 ? (
              <div
                className="panel-bottombar__avatar"
                title="Çevrimiçi kimse yok"
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 999,
                    background: "#e5e7eb",
                    color: "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 900,
                  }}
                >
                  —
                </div>
              </div>
            ) : (
              onlineUsers.slice(0, 8).map((u) => (
                <div
                  key={u.id}
                  className="panel-bottombar__avatar"
                  title={u.name}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 999,
                      background: avatarColor(u.name),
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    {getInitials(u.name)}
                  </div>
                </div>
              ))
            )}
            {onlineUsers.length > 8 && (
              <div className="panel-bottombar__avatar-count">
                +{onlineUsers.length - 8}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isQuickTaskOpen}
        onClose={() => {
          setIsQuickTaskOpen(false);
          setQuickTaskError("");
        }}
        title="Hızlı Görev Ekle"
        description="Bu görev bulunduğunuz sayfaya bağlanır."
        size="lg"
        position="center"
        buttons={[
          {
            label: isQuickTaskAdding ? "Ekleniyor..." : "Görevi Ekle",
            onClick: submitQuickTask,
            variant: "primary",
            disabled: isQuickTaskAdding,
          },
        ]}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 12,
            padding: "8px 10px",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            background: "#f8fafc",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <i className="fa-duotone fa-link" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {currentTaskPageTitle()} · {pathname}
            </span>
          </span>
          <Link href="/pages/users/task" style={{ color: "#2563eb", fontWeight: 800, textDecoration: "none", whiteSpace: "nowrap" }}>
            Liste
          </Link>
        </div>
        <FormGroup>
          <Input
            label="Görev"
            value={quickTaskTitle}
            onChange={(v) => {
              setQuickTaskTitle(v);
              setQuickTaskError("");
            }}
            required
            error={quickTaskError}
            disabled={isQuickTaskAdding}
            inputRef={quickTaskRef}
            inputProps={{
              onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key !== "Enter") return;
                e.preventDefault();
                submitQuickTask();
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Textarea
            label="Görev Tanımı"
            value={quickTaskDescription}
            onChange={setQuickTaskDescription}
            rows={5}
            disabled={isQuickTaskAdding}
            placeholder="Bu sayfada ne yapılacak?"
          />
        </FormGroup>
      </Modal>

      <Modal
        isOpen={isSupportOpen}
        onClose={() => {
          setIsSupportOpen(false);
          resetSupportForm();
        }}
        title="Destek Talebi Oluştur"
        description="Talebinizi gönderin, ekip en kısa sürede yanıtlasın."
        position="right"
        size="lg"
      >
        <FormGroup>
          <Textarea
            label="Talep Detayı"
            value={supportMessage}
            onChange={(v) => {
              setSupportMessage(v);
              if (supportError) setSupportError("");
            }}
            required
            rows={6}
            disabled={isSupportSubmitting}
            helperText="Ne yaptınız, nerede sorun yaşadınız, beklediğiniz sonuç neydi?"
          />
        </FormGroup>

        <FormGroup>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
              Dosya / Ekran Görüntüsü
            </label>
            <input
              ref={supportFileRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.xls,.xlsx,.csv"
              disabled={isSupportSubmitting}
              onChange={onSupportFileChange}
              style={{ display: "none" }}
            />
            <button
              type="button"
              onClick={() => supportFileRef.current?.click()}
              disabled={isSupportSubmitting}
              style={{
                border: "1px dashed #94a3b8",
                borderRadius: 10,
                padding: "12px 14px",
                background: "#f8fafc",
                color: "#334155",
                fontWeight: 600,
                cursor: isSupportSubmitting ? "not-allowed" : "pointer",
                textAlign: "left",
              }}
            >
              Dosya Ekle (Excel, PDF, Görsel, GIF/JPEG, Video)
            </button>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Birden fazla dosya ekleyebilirsiniz. En fazla 5 dosya, dosya
              başına 50MB.
            </div>
            {supportFiles.length > 0 && (
              <div style={{ display: "grid", gap: 6 }}>
                {supportFiles.map((f) => (
                  <div
                    key={f.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "6px 10px",
                      background: "#f8fafc",
                    }}
                  >
                    <div style={{ minWidth: 0, display: "grid", gap: 2 }}>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#334155",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 300,
                          fontWeight: 600,
                        }}
                      >
                        {f.name}
                      </span>
                      <span style={{ fontSize: 11, color: "#64748b" }}>
                        {formatBytes(f.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSupportFile(f.id)}
                      disabled={isSupportSubmitting}
                      style={{
                        border: 0,
                        background: "transparent",
                        color: "#ef4444",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      Kaldır
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormGroup>

        {supportError && (
          <div style={{ color: "#dc2626", fontSize: 13, marginTop: 6 }}>
            {supportError}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 14,
          }}
        >
          <button
            type="button"
            onClick={() => {
              setIsSupportOpen(false);
              resetSupportForm();
            }}
            disabled={isSupportSubmitting}
            style={{
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#334155",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={submitSupportRequest}
            disabled={isSupportSubmitting}
            style={{
              border: "1px solid #4f46e5",
              background: "#4f46e5",
              color: "#fff",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            {isSupportSubmitting ? "Gönderiliyor..." : "Talebi Gönder"}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isFaqOpen}
        onClose={() => setIsFaqOpen(false)}
        title="Sık Sorulan Sorular"
        description={
          faqState?.name
            ? `${faqState.name} sayfasına ait sorular`
            : "Bu sayfaya ait sorular"
        }
        position="right"
        size="lg"
      >
        <div className="bb-faq-panel">
          {isAdmin && (
            <div className="bb-faq-actions">
              <button
                type="button"
                onClick={openFaqQuestionModal}
                disabled={!faqState?.nav_id || isFaqSaving}
              >
                <i className="fa-duotone fa-plus" /> Soru Ekle
              </button>
            </div>
          )}

          {!faqState?.nav_id ? (
            <div className="bb-faq-empty">
              Bu sayfa için menü kaydı bulunamadı.
            </div>
          ) : (faqState.faqs || []).length === 0 ? (
            <div className="bb-faq-empty">
              Bu sayfa için sık sorulan soru bulunmuyor.
              {isAdmin ? " Soru ekleyerek bu alanı aktif edebilirsiniz." : ""}
            </div>
          ) : (
            <div className="bb-faq-list">
              {faqState.faqs.map((item) => {
                const hasAnswer = Boolean(item.answer_html?.trim());
                return (
                  <div
                    key={item.id}
                    className={`bb-faq-row ${draggedFaqId === item.id ? "is-dragging" : ""}`}
                    draggable={isAdmin}
                    onDragStart={() => setDraggedFaqId(item.id)}
                    onDragEnd={() => setDraggedFaqId(null)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => handleFaqDrop(event, item.id)}
                  >
                    {isAdmin && (
                      <span className="bb-faq-row__drag" title="Sırala">
                        <i className="fa-duotone fa-grip-dots-vertical" />
                      </span>
                    )}
                    <span
                      className={`bb-faq-row__status ${hasAnswer ? "is-ready" : "is-missing"}`}
                      title={hasAnswer ? "Cevap girildi" : "Cevap bekliyor"}
                    />
                    <button
                      type="button"
                      className="bb-faq-row__question"
                      onClick={() => (isAdmin ? openFaqEditor(item) : null)}
                    >
                      <span>{item.question}</span>
                      {!isAdmin && <i className="fa-duotone fa-chevron-down" />}
                    </button>
                    {!isAdmin && (
                      <div
                        className="bb-faq-row__answer"
                        dangerouslySetInnerHTML={{
                          __html:
                            item.answer_html || "<p>Yanıt hazırlanıyor.</p>",
                        }}
                      />
                    )}
                    {isAdmin && (
                      <div className="bb-faq-row__tools">
                        <button
                          type="button"
                          title="Sil"
                          className="danger"
                          onClick={() => setDeletingFaq(item)}
                          disabled={isFaqSaving}
                        >
                          <i className="fa-duotone fa-trash" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isFaqQuestionOpen}
        onClose={() => setIsFaqQuestionOpen(false)}
        title="Soru Ekle"
        description="Bu sayfada görünecek soruyu yazın."
        position="right"
        size="md"
        buttons={[
          {
            label: isFaqSaving ? "Ekleniyor..." : "Soruyu Ekle",
            variant: "primary",
            onClick: saveNewFaqQuestion,
            disabled: isFaqSaving,
          },
        ]}
      >
        <div className="bb-faq-edit-modal">
          <FormGroup>
            <Input
              label="Soru"
              value={newFaqQuestion}
              onChange={(value) => {
                setNewFaqQuestion(value);
                setNewFaqQuestionError("");
              }}
              inputProps={{
                onKeyDown: (event) => {
                  if (event.key !== "Enter" || event.shiftKey) return;
                  event.preventDefault();
                  saveNewFaqQuestion();
                },
              }}
              required
              error={newFaqQuestionError}
            />
          </FormGroup>
          <div className="bb-faq-hint">
            Soru eklendikten sonra bu panel açık kalır; peş peşe soru
            girebilirsiniz.
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(deletingFaq)}
        onClose={() => setDeletingFaq(null)}
        title="Soruyu Sil"
        description="Bu soru ve girilmiş cevabı SSS listesinden kaldırılacak."
        position="center"
        size="sm"
        buttons={[
          {
            label: "Vazgeç",
            variant: "secondary",
            onClick: () => setDeletingFaq(null),
            disabled: isFaqSaving,
          },
          {
            label: isFaqSaving ? "Siliniyor..." : "Sil",
            variant: "danger",
            onClick: deleteFaqItem,
            disabled: isFaqSaving,
          },
        ]}
      >
        <div className="bb-faq-delete-confirm">
          <span>Silinecek soru</span>
          <strong>{deletingFaq?.question}</strong>
        </div>
      </Modal>

      <Modal
        isOpen={isFaqEditorOpen}
        onClose={() => setIsFaqEditorOpen(false)}
        title="Cevap Düzenle"
        description="Sorunun cevabını HTML içerik olarak hazırlayın."
        position="right"
        size="xl"
        buttons={[
          {
            label: isFaqSaving ? "Kaydediliyor..." : "Kaydet",
            variant: "primary",
            onClick: saveFaqItem,
            disabled: isFaqSaving,
          },
        ]}
      >
        <div className="bb-faq-edit-modal">
          <div className="bb-faq-answer-question">
            <span>Soru</span>
            <strong>{faqQuestion}</strong>
            {faqError && <em>{faqError}</em>}
          </div>
          <div className="bb-faq-edit-modal__label">Cevap</div>
          <FaqAnswerEditor
            value={faqAnswer}
            onChange={setFaqAnswer}
            onOpenUploadBox={openFaqUploadBox}
          />
        </div>
      </Modal>

      <Modal
        isOpen={isFaqUploadOpen}
        onClose={() => setIsFaqUploadOpen(false)}
        title="Uploadbox"
        description="Cevap içinde kullanacağınız görseli seçin veya yeni görsel yükleyin."
        position="right"
        size="lg"
      >
        <div className="bb-faq-uploadbox">
          <input
            ref={faqUploadFileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFaqUploadFile}
          />
          <button
            type="button"
            className="bb-faq-uploadbox__drop"
            onClick={() => faqUploadFileRef.current?.click()}
            disabled={isFaqUploadLoading}
          >
            <i className="fa-duotone fa-cloud-arrow-up" />
            <span>Yeni Görsel Yükle</span>
            <small>PNG, JPG, WEBP veya GIF dosyası seçebilirsiniz.</small>
          </button>
          <div className="bb-faq-uploadbox__list">
            {faqUploads.length === 0 ? (
              <div className="bb-faq-empty">
                {isFaqUploadLoading
                  ? "Görseller yükleniyor..."
                  : "Uploadbox içinde görsel bulunamadı."}
              </div>
            ) : (
              faqUploads.map((file) => (
                <div key={file.path} className="bb-faq-uploadbox__item">
                  <img src={uploadAssetUrl(file.path)} alt={file.name} />
                  <div>
                    <strong>{file.name}</strong>
                    <span>{formatBytes(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => insertFaqImage(file.path)}
                  >
                    Kullan
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(uploadAssetUrl(file.path));
                      showToast("Görsel bağlantısı kopyalandı", "success");
                    }}
                  >
                    Kopyala
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>

      <ChatDrawer />

      {showOrderFollowTour && isOrderDetailPage && orderFollow?.visible && (
        <div
          className="panel-order-follow-tour"
          role="dialog"
          aria-label="Sipariş takibi"
          style={orderFollowTourStyle}
        >
          <button
            type="button"
            className="panel-order-follow-tour__close"
            onClick={dismissOrderFollowTour}
            aria-label="Kapat"
          >
            <i className="fa-regular fa-xmark" />
          </button>
          <strong>Sipariş takibi burada</strong>
          <p>
            Bu ikona bastığınızda sipariş takibe alınır. Not eklendiğinde,
            iptal talebi oluştuğunda, paket veya durum değiştiğinde lojistik
            uygulamasından bildirim alırsınız.
          </p>
          <div className="panel-order-follow-tour__actions">
            {!orderFollow.isFollowing && (
              <button
                type="button"
                onClick={() => {
                  toggleOrderFollow();
                  dismissOrderFollowTour();
                }}
              >
                Takibe al
              </button>
            )}
            <button type="button" className="is-ghost" onClick={dismissOrderFollowTour}>
              Anladım
            </button>
          </div>
        </div>
      )}

      <style jsx global>{`
        .panel-bottombar__icon-btn.is-active {
          color: #2563eb;
          background: #eef4ff;
        }
        .panel-bottombar__icon-btn.is-ringing {
          color: #16a34a;
          background: #ecfdf5;
          box-shadow: 0 0 0 1px #86efac;
          animation: panel-phone-ring 780ms ease-in-out infinite;
        }
        .panel-bottombar__icon-btn.is-phone-active {
          color: #047857;
          background: #ecfdf5;
          box-shadow: 0 0 0 1px #bbf7d0;
        }
        .panel-bottombar__icon-btn.is-phone-inactive {
          color: #dc2626;
          background: #fff1f2;
          box-shadow: 0 0 0 1px #fecdd3;
        }
        @keyframes panel-phone-ring {
          0%, 100% { transform: rotate(0deg) scale(1); }
          15% { transform: rotate(-9deg) scale(1.04); }
          30% { transform: rotate(9deg) scale(1.04); }
          45% { transform: rotate(-7deg) scale(1.04); }
          60% { transform: rotate(7deg) scale(1.04); }
          75% { transform: rotate(0deg) scale(1); }
        }
        .panel-bottombar__order-follow {
          position: relative;
        }
        .panel-bottombar__order-follow.is-following {
          color: #047857;
          background: #ecfdf5;
          box-shadow: 0 0 0 1px #99f6e4;
        }
        .panel-bottombar__order-follow:disabled {
          opacity: 0.6;
          cursor: wait;
        }
        .panel-bottombar__order-follow-check {
          position: absolute;
          right: 3px;
          bottom: 3px;
          width: 12px;
          height: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          color: #fff;
          background: #10b981;
          box-shadow: 0 0 0 2px #fff;
        }
        .panel-bottombar__order-follow-check svg {
          width: 7px;
          height: 7px;
        }
        .panel-bottombar__order-follow.is-tour-target {
          animation: order-follow-pulse 1.2s ease-in-out infinite;
          z-index: 2;
        }
        @keyframes order-follow-pulse {
          0%, 100% { box-shadow: 0 0 0 1px #99f6e4, 0 0 0 0 rgba(16, 185, 129, 0.34); }
          50% { box-shadow: 0 0 0 1px #99f6e4, 0 0 0 8px rgba(16, 185, 129, 0); }
        }
        .panel-order-follow-tour {
          position: fixed;
          left: var(--order-follow-tour-left, max(14px, calc(100vw - 354px)));
          bottom: 54px;
          width: min(340px, calc(100vw - 28px));
          z-index: 4400;
          padding: 14px 16px 13px;
          border: 1px solid #c7f4df;
          border-radius: 10px;
          background: #f0fdf6;
          color: #1f2937;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
        }
        .panel-order-follow-tour::after {
          content: "";
          position: absolute;
          left: var(--order-follow-tour-arrow-left, 50%);
          bottom: -7px;
          width: 12px;
          height: 12px;
          background: #f0fdf6;
          border-right: 1px solid #c7f4df;
          border-bottom: 1px solid #c7f4df;
          transform: rotate(45deg);
        }
        .panel-order-follow-tour strong {
          display: block;
          padding-right: 26px;
          font-size: 13px;
          color: #065f46;
        }
        .panel-order-follow-tour p {
          margin: 7px 0 11px;
          font-size: 12px;
          line-height: 1.45;
          color: #475569;
        }
        .panel-order-follow-tour__close {
          position: absolute;
          top: 9px;
          right: 9px;
          width: 24px;
          height: 24px;
          border: 0;
          border-radius: 999px;
          background: transparent;
          color: #64748b;
          cursor: pointer;
        }
        .panel-order-follow-tour__close:hover {
          background: rgba(15, 23, 42, 0.06);
        }
        .panel-order-follow-tour__actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .panel-order-follow-tour__actions button {
          min-height: 30px;
          border: 1px solid #10b981;
          border-radius: 7px;
          padding: 0 11px;
          color: #fff;
          background: #10b981;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }
        .panel-order-follow-tour__actions button.is-ghost {
          color: #047857;
          background: #fff;
        }
        .bb-faq-panel {
          display: grid;
          gap: 12px;
        }
        .bb-faq-actions {
          display: flex;
          justify-content: flex-end;
        }
        .bb-faq-actions button,
        .bb-faq-row__tools button,
        .bb-faq-editor__tools button {
          border: 1px solid #dbe3f5;
          background: #fff;
          color: #26356f;
          cursor: pointer;
        }
        .bb-faq-actions button {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          height: 34px;
          padding: 0 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .bb-faq-actions button:disabled,
        .bb-faq-row__tools button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .bb-faq-empty {
          border: 1px dashed #cbd6ee;
          background: #f8faff;
          color: #6b789f;
          padding: 18px;
          font-size: 13px;
          line-height: 1.5;
        }
        .bb-faq-list {
          display: grid;
          border: 1px solid #edf1fa;
        }
        .bb-faq-row {
          display: grid;
          grid-template-columns: auto auto minmax(0, 1fr) auto;
          gap: 9px;
          align-items: center;
          border-bottom: 1px solid #edf1fa;
          padding: 12px;
          background: #fff;
          transition:
            background 160ms ease,
            opacity 160ms ease;
        }
        .bb-faq-row.is-dragging {
          opacity: 0.45;
          background: #f6f9ff;
        }
        .bb-faq-row:last-child {
          border-bottom: 0;
        }
        .bb-faq-row__drag {
          color: #9aa8cd;
          cursor: grab;
          font-size: 14px;
        }
        .bb-faq-row__status {
          width: 11px;
          height: 11px;
          border-radius: 999px;
          box-shadow: 0 0 0 3px #f7f9ff;
        }
        .bb-faq-row__status.is-ready {
          background: #16a34a;
        }
        .bb-faq-row__status.is-missing {
          background: #ef4444;
        }
        .bb-faq-row__question {
          border: 0;
          background: transparent;
          padding: 0;
          color: #26356f;
          font-size: 13px;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
        }
        .bb-faq-row__answer {
          grid-column: 3 / -1;
          color: #4d5a86;
          font-size: 12px;
          line-height: 1.55;
        }
        .bb-faq-row__tools {
          display: inline-flex;
          gap: 5px;
        }
        .bb-faq-row__tools button {
          width: 28px;
          height: 28px;
        }
        .bb-faq-row__tools button.danger {
          color: #dc2626;
          border-color: #fecaca;
        }
        .bb-faq-edit-modal {
          display: grid;
          gap: 12px;
        }
        .bb-faq-edit-modal__label {
          color: #26356f;
          font-size: 12px;
          font-weight: 600;
        }
        .bb-faq-hint {
          border: 1px solid #dbeafe;
          background: #eff6ff;
          color: #3f5d96;
          padding: 10px 12px;
          font-size: 12px;
          line-height: 1.45;
        }
        .bb-faq-answer-question {
          display: grid;
          gap: 6px;
          border: 1px solid #dbe3f5;
          background: #f8faff;
          padding: 12px;
        }
        .bb-faq-answer-question span {
          color: #7b89b2;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .bb-faq-answer-question strong {
          color: #26356f;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.35;
        }
        .bb-faq-answer-question em {
          color: #dc2626;
          font-style: normal;
          font-size: 12px;
        }
        .bb-faq-delete-confirm {
          display: grid;
          gap: 8px;
          border: 1px solid #fee2e2;
          background: #fff7f7;
          padding: 12px;
        }
        .bb-faq-delete-confirm span {
          color: #991b1b;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .bb-faq-delete-confirm strong {
          color: #26356f;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.4;
        }
        .bb-faq-editor {
          border: 1px solid #dbe3f5;
          background: #fff;
        }
        .bb-faq-editor__toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 8px;
          border-bottom: 1px solid #edf1fa;
          background: #f8faff;
        }
        .bb-faq-editor__tools {
          display: flex;
          gap: 5px;
          align-items: center;
        }
        .bb-faq-editor__tools button {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition:
            background 160ms ease,
            border-color 160ms ease,
            color 160ms ease;
        }
        .bb-faq-editor__tools button:hover:not(:disabled),
        .bb-faq-editor__tools button.is-active {
          background: #2563eb;
          border-color: #2563eb;
          color: #fff;
        }
        .bb-faq-editor__tools button:disabled {
          cursor: not-allowed;
          opacity: 0.45;
        }
        .bb-faq-editor__tools--right {
          margin-left: auto;
        }
        .bb-faq-editor__tools button.bb-faq-editor__upload {
          width: auto;
          height: 30px;
          padding: 0 11px;
          gap: 6px;
          background: linear-gradient(135deg, #2563eb, #4f46e5);
          border-color: #2563eb;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          box-shadow: 0 8px 18px rgba(37, 99, 235, 0.2);
        }
        .bb-faq-editor__surface {
          min-height: 360px;
          max-height: 56vh;
          overflow: auto;
          padding: 14px;
          outline: none;
          color: #1f2937;
          font-size: 13px;
          line-height: 1.65;
        }
        .bb-faq-editor__source {
          min-height: 360px;
          max-height: 56vh;
          width: 100%;
          border: 0;
          outline: 0;
          resize: vertical;
          padding: 14px;
          background: #0b1020;
          color: #e5e7eb;
          font-size: 12px;
          line-height: 1.6;
          font-family:
            ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
            "Liberation Mono", "Courier New", monospace;
        }
        .bb-faq-uploadbox {
          display: grid;
          gap: 14px;
        }
        .bb-faq-uploadbox__drop {
          display: grid;
          justify-items: center;
          gap: 8px;
          min-height: 138px;
          border: 1px dashed #2563eb;
          background: linear-gradient(180deg, #eff6ff, #ffffff);
          color: #26356f;
          cursor: pointer;
          padding: 22px;
        }
        .bb-faq-uploadbox__drop i {
          color: #2563eb;
          font-size: 30px;
        }
        .bb-faq-uploadbox__drop span {
          font-size: 14px;
          font-weight: 600;
        }
        .bb-faq-uploadbox__drop small {
          color: #8491b7;
          font-size: 12px;
        }
        .bb-faq-uploadbox__list {
          display: grid;
          border: 1px solid #edf1fa;
          max-height: calc(100vh - 280px);
          overflow: auto;
        }
        .bb-faq-uploadbox__item {
          display: grid;
          grid-template-columns: 54px minmax(0, 1fr) auto auto;
          gap: 10px;
          align-items: center;
          padding: 10px;
          border-bottom: 1px solid #edf1fa;
        }
        .bb-faq-uploadbox__item:last-child {
          border-bottom: 0;
        }
        .bb-faq-uploadbox__item img {
          width: 54px;
          height: 54px;
          object-fit: cover;
          border: 1px solid #dbe3f5;
          background: #f8faff;
        }
        .bb-faq-uploadbox__item div {
          display: grid;
          min-width: 0;
          gap: 3px;
        }
        .bb-faq-uploadbox__item strong {
          color: #26356f;
          font-size: 12px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .bb-faq-uploadbox__item span {
          color: #8a96bc;
          font-size: 11px;
        }
        .bb-faq-uploadbox__item button {
          border: 1px solid #dbe3f5;
          background: #fff;
          color: #26356f;
          height: 30px;
          padding: 0 10px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
        }
        .bb-faq-uploadbox__item button:first-of-type {
          background: #2563eb;
          border-color: #2563eb;
          color: #fff;
        }
      `}</style>
    </>
  );
}
